import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Send, Upload, FileSpreadsheet, X, Pencil, Trash2, LayoutList, Rows3, Search, ChevronUp, ChevronDown, Plane, Building2, MapPin, Clock, Calendar, Ticket, StickyNote } from "lucide-react";
import CollapsibleForm from "../../components/CollapsibleForm";

// Turn "John Smith" → "JS" for the avatar initials on each arrival card.
// Falls back to "?" for empty names so the layout never breaks.
const initialsOf = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

// Human-friendly date: "15 Mar 2026" / "—".
const prettyDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

export default function ArrivalInfoTab({ apps, staffPrograms, staffWeekends: _staffWeekends, onSubmitted }) {
  // Any application that's still in the pipeline (pending or approved)
  // counts as a participant here — a partner should be able to submit /
  // edit arrival info the moment they submit the application, without
  // waiting for Founder approval. Only declined / cancelled apps are
  // excluded so the dropdown doesn't clutter with dead rows.
  const liveApps = (apps || []).filter(a => a.status !== "declined" && a.status !== "cancelled" && a.status !== "rejected");
  // Flatten only the primary program participants (weekend bookings are
  // intentionally excluded from the arrival-info dropdown — arrival details
  // are for a participant's main programme entry, not their weekend trip).
  const participants = [
    ...liveApps.map(a => ({
      key: `app:${a.id}`, ref_type: "application", ref_id: a.id,
      label: `${a.name} · ${a.program} · ${a.start_date || "—"}${a.status === "pending" ? "  ·  (Pending approval)" : ""}`,
      customer: a.name,
    })),
    ...(staffPrograms || []).map(p => ({
      key: `prog:${p.id}`, ref_type: "program", ref_id: p.id,
      label: `${p.customer} · ${p.programme} · ${p.start_date || "—"}  ·  (Booked by our team)`,
      customer: p.customer,
    })),
  ];

  const [existing, setExisting] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const emptyForm = { flight_date: "", flight_time: "", airline: "", flight_number: "", arrival_airport: "", hotel_name: "", hotel_address: "", hotel_pickup_time: "", pickup_from: "", notes: "" };
  const [f, setF] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run preview payload
  const [viewMode, setViewMode] = useState("card"); // "card" | "table"
  const [selected, setSelected] = useState(() => new Set());
  const [filterQuery, setFilterQuery] = useState("");
  const [sortBy, setSortBy] = useState({ col: "flight_date", dir: "asc" });
  const fileRef = useRef(null);

  const reload = () => api.get("/partner-portal/arrivals").then(({ data }) => setExisting(data || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  // Only partner-owned rows are deletable — everything else came from staff.
  const ownedRows = existing.filter(e => !!e.source_partner_id);

  // Filter → sort pipeline.  Filter matches customer, flight number, airline,
  // or hotel (case-insensitive) so a partner can pull one cohort out of 100+
  // by typing "AI-101", "Golden Guest", or a customer name.  Sort applies to
  // both card and table views so the ordering is consistent.
  const visibleRows = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    let rows = existing;
    if (q) {
      rows = rows.filter(r => (
        (r.customer || "").toLowerCase().includes(q) ||
        (r.flight_number || "").toLowerCase().includes(q) ||
        (r.airline || "").toLowerCase().includes(q) ||
        (r.hotel_name || "").toLowerCase().includes(q)
      ));
    }
    const { col, dir } = sortBy;
    const mul = dir === "desc" ? -1 : 1;
    rows = [...rows].sort((a, b) => {
      const av = (a[col] || "").toString().toLowerCase();
      const bv = (b[col] || "").toString().toLowerCase();
      if (av < bv) return -1 * mul;
      if (av > bv) return 1 * mul;
      return 0;
    });
    return rows;
  }, [existing, filterQuery, sortBy]);

  const toggleSort = useCallback((col) => setSortBy(prev =>
    prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }
  ), []);
  // Plain helper — returns JSX inline so the react/no-unstable-nested-components
  // rule stays happy (a nested Component definition would re-mount on every render).
  const sortIcon = (col) => sortBy.col !== col ? null :
    sortBy.dir === "asc" ? <ChevronUp size={11} className="inline -mt-0.5"/> : <ChevronDown size={11} className="inline -mt-0.5"/>;

  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllOwned = () => setSelected(prev =>
    ownedRows.length > 0 && ownedRows.every(r => prev.has(r.id))
      ? new Set()
      : new Set(ownedRows.map(r => r.id))
  );
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} arrival row${selected.size === 1 ? "" : "s"}? This action cannot be undone.`)) return;
    const ids = [...selected];
    try {
      const results = await Promise.allSettled(ids.map(id => api.delete(`/partner-portal/arrivals/${id}`)));
      const ok = results.filter(r => r.status === "fulfilled").length;
      const fail = ids.length - ok;
      if (ok > 0) toast.success(`${ok} arrival${ok === 1 ? "" : "s"} deleted${fail ? ` · ${fail} failed` : ""}`);
      if (fail && ok === 0) toast.error("Bulk delete failed");
      setSelected(new Set());
      await reload();
    } catch { toast.error("Bulk delete failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/partner-portal/arrivals/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "aii-partner-arrivals-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error("Could not download template"); }
  };

  const uploadFile = async (file, { commit = false } = {}) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const url = commit
        ? "/partner-portal/arrivals/upload"
        : "/partner-portal/arrivals/upload?dry_run=true";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!commit) {
        setPreview({ ...data, file });
      } else {
        const ids = data.arrival_ids || [];
        toast.success(`${data.created} arrivals saved · ${data.matched} matched to your participants${ids.length ? ` · view #${ids.length} in the list below` : ""}`);
        setPreview(null);
        await reload();
        onSubmitted?.();
        // Scroll the "Arrivals on file" section into view so the partner sees
        // the freshly uploaded rows without having to hunt.
        setTimeout(() => document.querySelector('[data-testid="arr-on-file"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const findExistingFor = (p) => {
    if (!p) return null;
    if (p.ref_type === "application") return existing.find(x => x.source_application_id === p.ref_id);
    return existing.find(x => x.source_booking_id === p.ref_id)
      || existing.find(x => (x.customer || "").trim().toLowerCase() === (p.customer || "").trim().toLowerCase());
  };

  const hydrateFrom = (key) => {
    setSelectedKey(key);
    const p = participants.find(x => x.key === key);
    const e = findExistingFor(p);
    if (e) {
      setF({
        flight_date: e.flight_date || "",
        // Some rows carry legacy "HH:MM:SS" values (Safari <input type=time>
        // and older CSV imports) — strip the seconds so the picker + display
        // both show the friendly "HH:MM" form.
        flight_time: (e.flight_time || "").slice(0, 5),
        airline: e.airline || "", flight_number: e.flight_number || "",
        arrival_airport: e.arrival_airport || "",
        hotel_name: e.hotel_name || "", hotel_address: e.hotel_address || "",
        hotel_pickup_time: (e.hotel_pickup_time || "").slice(0, 5),
        pickup_from: e.pickup_from || e.arrival_type || "",
        notes: e.notes || "",
      });
    } else {
      setF(emptyForm);
    }
  };

  const submit = async () => {
    const p = participants.find(x => x.key === selectedKey);
    if (!p) return toast.error("Choose a participant first");
    const payload = { ...f };
    if (p.ref_type === "application") payload.application_id = p.ref_id;
    else { payload.booking_id = p.ref_id; payload.booking_type = p.ref_type; }
    setBusy(true);
    try {
      await api.post("/partner-portal/arrivals", payload);
      toast.success("Arrival info saved — team notified");
      await reload();
      onSubmitted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const editRow = (row) => {
    // Locate matching participant so form + selector rehydrate together.
    let p = null;
    if (row.source_application_id) p = participants.find(x => x.ref_type === "application" && x.ref_id === row.source_application_id);
    if (!p && row.source_booking_id) p = participants.find(x => (x.ref_type === "program" || x.ref_type === "weekend") && x.ref_id === row.source_booking_id);
    if (!p) p = participants.find(x => (x.customer || "").trim().toLowerCase() === (row.customer || "").trim().toLowerCase());
    if (!p) return toast.error("This arrival isn't linked to any of your active participants");
    hydrateFrom(p.key);
    // Scroll to top of tab so user sees the form
    setTimeout(() => document.querySelector('[data-testid="partner-arrivals"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  };

  if (participants.length === 0) return (
    <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]" data-testid="arr-empty">
      No participants yet — submit an application (or ask our team to book one under your account) and their arrival details will appear here automatically.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="partner-arrivals">
      <div className="aii-card p-4 border-l-4 border-brand" data-testid="arr-bulk-uploader">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold flex items-center gap-1.5"><Upload size={14} className="text-brand"/> Bulk upload arrival info</div>
            <p className="text-xs text-[color:var(--aii-muted)] mt-1">Excel or CSV. We match each row&apos;s <b>customer</b> against your approved participants (weekend-only rows are excluded — arrivals cover the primary programme).  Hit <b>Preview</b> first to see which rows match.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="arr-bulk-template" type="button" onClick={downloadTemplate}
               className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1"
               title="Download the Excel template">
              <FileSpreadsheet size={12}/> Template
            </button>
            <button data-testid="arr-bulk-upload" disabled={busy} onClick={() => fileRef.current?.click()}
                    className="text-xs px-3 py-1.5 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1 disabled:opacity-50">
              <Upload size={12}/> {busy ? "Working…" : "Upload Excel"}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                   onChange={e => uploadFile(e.target.files?.[0])}/>
          </div>
        </div>
      </div>

      <CollapsibleForm
        title="Arrival information"
        hint="Choose any of your participants and add or edit their flight + hotel-pickup details. The team is notified on every save."
        testid="arr-form"
        icon={Plane}
        defaultOpen={false}
      >
        <select className="input-aii" value={selectedKey} onChange={e => hydrateFrom(e.target.value)} data-testid="arr-participant">
          <option value="">Choose participant…</option>
          {participants.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className="input-aii" type="date" value={f.flight_date} onChange={e => setF({ ...f, flight_date: e.target.value })} placeholder="Flight date"/>
          <input className="input-aii" type="time" value={f.flight_time} onChange={e => setF({ ...f, flight_time: e.target.value })} placeholder="Flight time"/>
          <input className="input-aii" placeholder="Airline" value={f.airline} onChange={e => setF({ ...f, airline: e.target.value })}/>
          <input className="input-aii" placeholder="Flight #" value={f.flight_number} onChange={e => setF({ ...f, flight_number: e.target.value })}/>
          <input className="input-aii sm:col-span-2" placeholder="Arrival airport (e.g. DEL / KTM)" value={f.arrival_airport} onChange={e => setF({ ...f, arrival_airport: e.target.value })}/>
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold sm:col-span-2">Pick-up from
            <select data-testid="arr-pickup-from" className="input-aii mt-1" value={f.pickup_from} onChange={e => setF({ ...f, pickup_from: e.target.value })}>
              <option value="">Choose pickup point…</option>
              <option value="Airport">Airport</option>
              <option value="Hotel">Hotel</option>
              <option value="Railway Station">Railway Station</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <input className="input-aii" placeholder="Hotel / accommodation name" value={f.hotel_name} onChange={e => setF({ ...f, hotel_name: e.target.value })}/>
          <input className="input-aii" type="time" value={f.hotel_pickup_time} onChange={e => setF({ ...f, hotel_pickup_time: e.target.value })} placeholder="Hotel pickup time"/>
          <input className="input-aii sm:col-span-2" placeholder="Hotel address" value={f.hotel_address} onChange={e => setF({ ...f, hotel_address: e.target.value })}/>
          <textarea className="input-aii sm:col-span-2" rows={2} placeholder="Notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}/>
        </div>
        <button onClick={submit} disabled={busy} data-testid="arr-submit" className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50 flex items-center gap-2">
          <Send size={13}/> {busy ? "Saving…" : "Save arrival info"}
        </button>
      </CollapsibleForm>

      {existing.length > 0 && (
        <div data-testid="arr-on-file">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h3 className="font-semibold text-sm">Arrivals on file · click a row to edit</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--aii-muted)]"/>
                <input
                  data-testid="arr-filter"
                  type="search"
                  value={filterQuery}
                  onChange={e => setFilterQuery(e.target.value)}
                  placeholder="Filter arrivals…"
                  className="pl-7 pr-3 py-1.5 text-xs border border-[color:var(--aii-border)] rounded-md bg-white w-52"
                />
              </div>
              <div className="flex items-center gap-1 bg-[color:var(--aii-bg)] rounded-md p-0.5" data-testid="arr-view-toggle">
                <button data-testid="arr-view-card" type="button" onClick={() => setViewMode("card")} title="Card view"
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 font-semibold ${viewMode === "card" ? "bg-white shadow text-brand" : "text-[color:var(--aii-muted)] hover:text-brand"}`}>
                  <LayoutList size={12}/> Cards
                </button>
                <button data-testid="arr-view-table" type="button" onClick={() => setViewMode("table")} title="Compact table view — good for 50+ arrivals"
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 font-semibold ${viewMode === "table" ? "bg-white shadow text-brand" : "text-[color:var(--aii-muted)] hover:text-brand"}`}>
                  <Rows3 size={12}/> Table
                </button>
              </div>
            </div>
          </div>

          {visibleRows.length === 0 && (
            <div className="aii-card p-6 text-center text-sm italic text-[color:var(--aii-muted)]" data-testid="arr-filter-empty">
              No arrivals match &ldquo;{filterQuery}&rdquo;.
            </div>
          )}

          {visibleRows.length > 0 && viewMode === "card" ? (() => {
            // Group arrival cards by exact flight_date so partners see all
            // arrivals sharing an airport day clustered together (matches
            // MyApplications + WeekendTours behaviour). Rows without a
            // flight_date land under "No flight date" at the bottom.
            const groups = new Map();
            for (const e of visibleRows) {
              const k = (e.flight_date || "").slice(0, 10) || "No flight date";
              if (!groups.has(k)) groups.set(k, []);
              groups.get(k).push(e);
            }
            const prettyDate = (iso) => {
              if (!iso || iso === "No flight date") return "No flight date";
              const d = new Date(`${iso}T00:00:00`);
              if (Number.isNaN(d.getTime())) return iso;
              return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
            };
            const sortedKeys = [...groups.keys()].sort((a, b) => {
              // Push "No flight date" bucket to the bottom.
              if (a === "No flight date") return 1;
              if (b === "No flight date") return -1;
              return a.localeCompare(b);
            });
            return (
              <div className="space-y-4">
                {sortedKeys.map(k => (
                  <div key={`arr-grp-${k}`} className="space-y-2" data-testid={`arr-date-group-${k}`}>
                    <div className="text-[11px] uppercase tracking-widest font-bold text-brand pt-1 pb-1 border-b border-[color:var(--aii-border)] flex items-center gap-2" data-testid={`arr-date-hdr-${k}`}>
                      <span>{prettyDate(k)}</span>
                      <span className="text-[10px] font-normal text-[color:var(--aii-muted)] normal-case tracking-normal">
                        · {groups.get(k).length} arrival{groups.get(k).length === 1 ? "" : "s"}
                      </span>
                    </div>
                    {groups.get(k).map(e => {
              const badge = e.source_partner_id
                  ? { text: "Partner", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" }
                  : { text: "Team", cls: "bg-slate-100 text-slate-700 border-slate-200" };
                const owned = !!e.source_partner_id;
                const flightLine = [e.airline, e.flight_number].filter(Boolean).join(" · ");
                const isSelected = selected.has(e.id);
                return (
                  <div
                    key={e.id}
                    data-testid={`arr-row-${e.id}`}
                    className={`aii-card p-4 text-sm hover:border-brand hover:shadow-sm transition ${isSelected ? "border-brand bg-[color:var(--aii-green-tint)]/40" : ""}`}
                  >
                    {/* header row: avatar + customer + source + actions */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1 flex items-start gap-3">
                        {owned && (
                          <input
                            type="checkbox"
                            data-testid={`arr-select-${e.id}`}
                            checked={isSelected}
                            onChange={() => toggleRow(e.id)}
                            onClick={ev => ev.stopPropagation()}
                            className="mt-1"
                            title="Select for bulk delete"
                          />
                        )}
                        <div className="w-10 h-10 rounded-full bg-[color:var(--aii-green-tint)] text-brand font-display font-bold text-sm flex items-center justify-center flex-shrink-0" aria-hidden="true">
                          {initialsOf(e.customer)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-display font-semibold text-base truncate">{e.customer || "—"}</div>
                          <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border ${badge.cls}`}>Source · {badge.text}</span>
                            {e.programme && <span>· {e.programme}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          data-testid={`arr-edit-${e.id}`}
                          type="button"
                          onClick={() => editRow(e)}
                          title="Edit this arrival row"
                          className="text-[color:var(--aii-muted)] hover:text-brand p-1.5 rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1 text-xs font-semibold"
                        >
                          <Pencil size={13}/> Edit
                        </button>
                        {owned && (
                          <button
                            data-testid={`arr-delete-${e.id}`}
                            type="button"
                            onClick={async (ev) => {
                              ev.stopPropagation();
                              if (!window.confirm(`Delete arrival info for ${e.customer}?`)) return;
                              try {
                                await api.delete(`/partner-portal/arrivals/${e.id}`);
                                toast.success("Arrival deleted");
                                await reload();
                              } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
                            }}
                            title="Delete this arrival row"
                            className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5 rounded hover:bg-red-50 flex items-center gap-1 text-xs font-semibold"
                          >
                            <Trash2 size={13}/> Delete
                          </button>
                        )}
                      </div>
                    </div>

                    {/* info grid: Flight · Hotel · Pickup */}
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {/* Flight */}
                      <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-sky-50/40">
                        <div className="text-[9px] uppercase tracking-widest text-sky-700 font-bold flex items-center gap-1 mb-1">
                          <Plane size={10}/> Flight
                        </div>
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                          <Calendar size={11} className="text-[color:var(--aii-muted)]"/> {prettyDate(e.flight_date)}
                          {e.flight_time && <span className="text-[color:var(--aii-muted)] font-normal">· {(e.flight_time || "").slice(0,5)}</span>}
                        </div>
                        {flightLine && (
                          <div className="text-[11px] text-[color:var(--aii-text-2)] mt-0.5 truncate" title={flightLine}>{flightLine}</div>
                        )}
                        {e.arrival_airport && (
                          <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1">
                            <Ticket size={9}/> Arrives {e.arrival_airport}
                          </div>
                        )}
                      </div>

                      {/* Hotel */}
                      <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-amber-50/40">
                        <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1 mb-1">
                          <Building2 size={10}/> Hotel
                        </div>
                        {e.hotel_name ? (
                          <>
                            <div className="text-sm font-semibold truncate" title={e.hotel_name}>{e.hotel_name}</div>
                            {e.hotel_address && (
                              <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 truncate" title={e.hotel_address}>{e.hotel_address}</div>
                            )}
                          </>
                        ) : (
                          <div className="text-[11px] text-[color:var(--aii-muted)] italic">Not provided</div>
                        )}
                      </div>

                      {/* Pickup */}
                      <div className="border border-[color:var(--aii-border)] rounded-md p-2.5 bg-emerald-50/40">
                        <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1 mb-1">
                          <MapPin size={10}/> Pickup
                        </div>
                        {(e.pickup_from || e.arrival_type || e.hotel_pickup_time) ? (
                          <>
                            <div className="text-sm font-semibold flex items-center gap-1">
                              {e.pickup_from || e.arrival_type || "TBD"}
                            </div>
                            {e.hotel_pickup_time && (
                              <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1">
                                <Clock size={9}/> {(e.hotel_pickup_time || "").slice(0,5)}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-[11px] text-[color:var(--aii-muted)] italic">Not provided</div>
                        )}
                      </div>
                    </div>

                    {e.notes && (
                      <div className="mt-2 text-[11px] text-[color:var(--aii-text-2)] bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded px-2 py-1.5 flex items-start gap-1.5">
                        <StickyNote size={11} className="mt-0.5 text-[color:var(--aii-muted)] flex-shrink-0"/>
                        <span className="whitespace-pre-wrap break-words">{e.notes}</span>
                      </div>
                    )}
                  </div>
                );
              })}
                  </div>
                ))}
              </div>
            );
          })() : (
            <div className="aii-card overflow-x-auto">
              <table className="w-full text-xs" data-testid="arr-table">
                <thead className="bg-[color:var(--aii-bg)]">
                  <tr className="text-left">
                    <th className="px-3 py-2 w-8">
                      {ownedRows.length > 0 && (
                        <input type="checkbox" data-testid="arr-select-all"
                               checked={ownedRows.every(r => selected.has(r.id))}
                               onChange={selectAllOwned}
                               title="Select all deletable rows"/>
                      )}
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none hover:text-brand" data-testid="arr-sort-customer"
                        onClick={() => toggleSort("customer")}>Customer {sortIcon("customer")}</th>
                    <th className="px-3 py-2 cursor-pointer select-none hover:text-brand" data-testid="arr-sort-flight_date"
                        onClick={() => toggleSort("flight_date")}>Flight {sortIcon("flight_date")}</th>
                    <th className="px-3 py-2 cursor-pointer select-none hover:text-brand" data-testid="arr-sort-flight_number"
                        onClick={() => toggleSort("flight_number")}>Flight # {sortIcon("flight_number")}</th>
                    <th className="px-3 py-2 cursor-pointer select-none hover:text-brand" data-testid="arr-sort-flight_time"
                        onClick={() => toggleSort("flight_time")}>Time {sortIcon("flight_time")}</th>
                    <th className="px-3 py-2 cursor-pointer select-none hover:text-brand" data-testid="arr-sort-hotel_name"
                        onClick={() => toggleSort("hotel_name")}>Hotel {sortIcon("hotel_name")}</th>
                    <th className="px-3 py-2">Pickup</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {existing.map(e => {
                    const owned = !!e.source_partner_id;
                    return (
                      <tr key={`t-${e.id}`} data-testid={`arr-trow-${e.id}`}
                          className={`border-t border-[color:var(--aii-border)]/50 ${selected.has(e.id) ? "bg-[color:var(--aii-green-tint)]/40" : ""}`}>
                        <td className="px-3 py-1.5">
                          {owned && (
                            <input type="checkbox" data-testid={`arr-tselect-${e.id}`}
                                   checked={selected.has(e.id)}
                                   onChange={() => toggleRow(e.id)}/>
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-medium">{e.customer}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{e.flight_date || "—"} {e.airline || ""}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap font-mono text-[11px]" data-testid={`arr-tflight-${e.id}`}>{e.flight_number || "—"}</td>
                        <td className="px-3 py-1.5">{(e.flight_time || "").slice(0,5) || "—"}</td>
                        <td className="px-3 py-1.5 truncate max-w-[10rem]">{e.hotel_name || "—"}</td>
                        <td className="px-3 py-1.5">{(e.hotel_pickup_time || "").slice(0,5) || "—"}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${owned ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                            {owned ? "Partner" : "Aii"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">
                          <button type="button" onClick={() => editRow(e)}
                                  className="text-[color:var(--aii-muted)] hover:text-brand p-1 rounded hover:bg-[color:var(--aii-green-tint)]"
                                  title="Edit"><Pencil size={13}/></button>
                          {owned && (
                            <button type="button"
                                    onClick={async () => {
                                      if (!window.confirm(`Delete arrival info for ${e.customer}?`)) return;
                                      try {
                                        await api.delete(`/partner-portal/arrivals/${e.id}`);
                                        toast.success("Arrival deleted");
                                        await reload();
                                      } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
                                    }}
                                    className="text-[color:var(--aii-muted)] hover:text-red-600 p-1 rounded hover:bg-red-50 ml-1"
                                    title="Delete"><Trash2 size={13}/></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-brand text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 z-40" data-testid="arr-bulk-del-bar">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <span className="text-white/50">·</span>
          <button data-testid="arr-bulk-del-apply" onClick={bulkDelete}
                  className="text-sm font-semibold px-3 py-1 rounded bg-white text-brand hover:bg-red-50 hover:text-red-700 flex items-center gap-1">
            <Trash2 size={12}/> Delete selected
          </button>
          <button data-testid="arr-bulk-del-clear" onClick={() => setSelected(new Set())}
                  className="text-xs text-white/70 hover:text-white p-1"><X size={13}/></button>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="arr-preview-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Upload size={16} className="text-brand"/> Preview arrival upload</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">We match each customer name to your approved participants.  Rows without a match are skipped.</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                  <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-semibold">{preview.previews?.length || 0} rows</span>
                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">{preview.matched} matched</span>
                  {preview.errors?.length > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 font-semibold">{preview.errors.length} skipped</span>}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {(preview.previews || []).length > 0 && (
                <table className="w-full text-xs" data-testid="arr-preview-table">
                  <thead className="bg-[color:var(--aii-bg)] sticky top-0"><tr className="text-left">
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Match</th>
                    <th className="px-3 py-2">Programme</th>
                    <th className="px-3 py-2">Flight</th>
                    <th className="px-3 py-2">Time</th>
                  </tr></thead>
                  <tbody>
                    {preview.previews.map((row, i) => (
                      <tr key={`ap-${i}`} className="border-t border-[color:var(--aii-border)]/50">
                        <td className="px-3 py-1.5 text-[color:var(--aii-muted)]">{row.row}</td>
                        <td className="px-3 py-1.5 font-medium">{row.customer}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${row.matched ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                            {row.matched ? row.kind : "no match"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 truncate max-w-[10rem]">{row.programme || "—"}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{row.flight_date || "—"} {row.airline || ""} {row.flight_number || ""}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap font-mono">{row.flight_time || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {(preview.errors || []).length > 0 && (
                <div className="p-3 border-t border-[color:var(--aii-border)] bg-red-50">
                  <div className="text-[11px] uppercase font-bold text-red-800 mb-1">Skipped rows</div>
                  <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                    {preview.errors.map((e, i) => <li key={`ae-${i}`}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button data-testid="arr-preview-cancel" onClick={() => setPreview(null)} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="arr-preview-apply" disabled={busy || preview.matched === 0}
                      onClick={() => uploadFile(preview.file, { commit: true })}
                      className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
                <Upload size={13}/> {busy ? "Uploading…" : `Apply · ${preview.matched} row${preview.matched === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
